import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimeoutOptions {
  timeout: number; // in milliseconds
  onIdle: () => void;
  onWarning?: () => void;
  warningTime?: number; // time before timeout to show warning (in milliseconds)
  isTransferActive?: boolean; // when true, suspend idle timer for the duration of any active transfer
}

export function useIdleTimeout({
  timeout,
  onIdle,
  onWarning,
  warningTime = 60000, // 1 minute warning by default
  isTransferActive,
}: UseIdleTimeoutOptions) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isTransferActiveRef = useRef<boolean>(false);

  // Keep isTransferActiveRef in sync with the prop without adding it to any useCallback dep array
  useEffect(() => {
    isTransferActiveRef.current = isTransferActive ?? false;
  }, [isTransferActive]);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (isTransferActiveRef.current) {
      clearTimers();
      return;
    }
    lastActivityRef.current = Date.now();
    clearTimers();

    // Set warning timer
    if (onWarning && warningTime < timeout) {
      warningRef.current = setTimeout(() => {
        onWarning();
      }, timeout - warningTime);
    }

    // Set idle timeout
    timeoutRef.current = setTimeout(() => {
      onIdle();
    }, timeout);
  }, [timeout, onIdle, onWarning, warningTime, clearTimers]);

  // When a transfer ends (isTransferActive transitions true → false), restart the timer
  // from Date.now() rather than from the last recorded user activity.
  useEffect(() => {
    if (!isTransferActive) {
      clearTimers();
      resetTimer();
    }
  }, [isTransferActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility change (tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we've been idle too long while tab was hidden
        const idleTime = Date.now() - lastActivityRef.current;
        if (idleTime >= timeout) {
          onIdle();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimers();
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [resetTimer, clearTimers, timeout, onIdle]);

  return {
    resetTimer,
    getLastActivity: () => lastActivityRef.current,
    getIdleTime: () => Date.now() - lastActivityRef.current,
  };
}
